// src/b2b/components/SelectMealPlan.jsx
import React, { useMemo, useState, useRef, useEffect } from 'react';

const eur = (n, currency = 'EUR') =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency })
    .format(Number(n || 0));

export default function SelectMealPlan({
  plans = [],              // [{code, desc, total, currency, included}]
  selectedCode,            // 👈 code du plan sélectionné (ex: state.selectedMealPlan.code)
  onSelect,
}) {
  const [selectedIdx, setSelectedIdx] = useState(null);
  const userPickedRef = useRef(false);

  // 1) plans triés : inclus d’abord, puis prix croissant
  const ordered = useMemo(() => {
    const arr = Array.isArray(plans) ? [...plans] : [];
    arr.sort(
      (a, b) =>
        Number(b.included) - Number(a.included) ||
        Number(a.total || 0) - Number(b.total || 0)
    );
    return arr;
  }, [plans]);

  // 2) index par défaut (plan inclus si présent)
  const defaultIdx = useMemo(() => {
    const i = ordered.findIndex((p) => p.included);
    return i >= 0 ? i : 0;
  }, [ordered]);

  // 3) sélection initiale / changement venant du parent
  useEffect(() => {
    if (!ordered.length) return;

    // 🔹 Cas 1 : le parent impose un plan (retour depuis /paiement)
    if (selectedCode) {
      const idx = ordered.findIndex((p) => p.code === selectedCode);
      if (idx >= 0 && selectedIdx !== idx) {
        const p = ordered[idx];
        setSelectedIdx(idx);
        userPickedRef.current = true;
        onSelect?.(p);
      }
      return; // on ne fait pas la sélection par défaut si selectedCode existe
    }

    // 🔹 Cas 2 : aucune sélection externe → plan inclus par défaut
    if (!userPickedRef.current && ordered.length) {
      if (selectedIdx !== defaultIdx) {
        setSelectedIdx(defaultIdx);
        onSelect?.(ordered[defaultIdx]);
      }
    }
  }, [ordered, selectedCode, defaultIdx, onSelect, selectedIdx]);

  if (!ordered.length) return null;

  return (
    <div className="vstack gap-2">
      {ordered.map((p, idx) => {
        const isSelected = selectedIdx === idx;
        const right = p.included ? (
          <span className="badge bg-success-subtle text-success border border-success-subtle">
            inclus
          </span>
        ) : (
          <span
            className="px-3 py-1 rounded text-white"
            style={{ background: '#0b1975' }}
          >
            +{eur(p.total, p.currency)}
          </span>
        );
        return (
          <label
            key={`${p.code || 'meal'}_${idx}`}
            className={`d-flex align-items-center justify-content-between border rounded px-3 py-2 ${
              isSelected ? 'bg-light border-primary' : 'border-200'
            }`}
            style={
              isSelected
                ? { boxShadow: '0 0 0 2px rgba(13,110,253,.25)' }
                : undefined
            }
          >
            <div className="d-flex align-items-center gap-2">
              <input
                type="radio"
                name="mealplan"
                checked={isSelected}
                onChange={() => {
                  userPickedRef.current = true;
                  setSelectedIdx(idx);
                  onSelect?.(p);
                }}
                className="mr-2"
              />
              <div className="fw-semibold">{p.desc}</div>
            </div>
            {right}
          </label>
        );
      })}
    </div>
  );
}
